import { IValidationState } from '../../../store/previousValidationSlice';

export interface IValidatorSuccess {
  newValues: Record<string, unknown>;
}

export interface IValidatorError {
  error: string;
  element?: HTMLElement | (() => HTMLElement | null);
}

export type TValidator = (
  values: Pick<IValidationState, 'submitValues' | 'values'>,
) => Promise<true | IValidatorSuccess | IValidatorError>;

export interface IValidatorObject {
  validator: TValidator;
  name: string;
  tabId?: string;
  form: string;
}

export function hasSucceedValidator(
  response: IValidatorError | IValidatorSuccess,
): response is IValidatorSuccess {
  return 'newValues' in response;
}

const validatorsStorage: Record<string, IValidatorObject[]> = {};
/**
 * @see useCustomField
 * @see useValidation
 *
 * Los validadores o "validators" son funciones que verifican que una parte
 * del formulario sea válida. Su uso es muy sencillo y a la vez potente.
 *
 * Cuando se requiere una validación sencilla, se recomienda el uso de
 * useCustomField pero cuando eso no es suficiente, los validadores son una
 * opción muy interesante.
 *
 * Básicamente, el validador es una función que acepta como parámetro un objeto
 * que contiene { values y submitValues }, que son los valores que el módulo
 * de validación fue acumulando durante el uso de la vista actual.
 *
 * El validador puede actuar a partir de estos valores si así se desea, pero
 * también a partir de valores del closure donde se ejecuta, esto da la ventaja
 * de validar contra campos muy complejos.
 *
 * @param validator -
 *
 * Esta función DEBE RETORNAR UNA PROMESA para estandarizar el funcionamiento
 * de los validadores. Dicha promesa puede ser resuelta con:
 *
 * - true en caso de que la validación haya salido exitosa y no se requieran
 * más cambios,
 *
 * - { newValues: Record<string,unknown> } en caso de que se desee anexar
 * nuevos valores al submitValues a partir de la actual validación.
 *
 * - { error: string, element? HTMLElement | (() => HTMLElement) } en caso de
 * que haya ocurrido un error. En este caso, el validador automáticamente
 * asignará el mensaje de error al estado de la validación, en caso de que
 * además se haya pasado element (un elemento HTML o una función que retorne
 * uno), se hará foco en dicho elemento y se marcará como inválido mediante
 * setCustomValidity y reportValidity.
 */
export function registerValidator(validator: IValidatorObject) {
  if (!validatorsStorage[validator.form])
    validatorsStorage[validator.form] = [];
  validatorsStorage[validator.form].push(validator);
}
export function unregisterValidator({
  form,
  name,
}: Pick<IValidatorObject, 'form' | 'name'>) {
  validatorsStorage[form] = validatorsStorage[form]?.filter(
    (current) => current.name !== name,
  );
}
export function getValidators(form: string) {
  if (validatorsStorage[form]) return [...validatorsStorage[form]];
  return [];
}
